/**
 * IntersectionObserver-based hook for scroll-triggered animations. Returns a
 * ref to attach to the element and a boolean indicating visibility.
 */
"use client";

import { useEffect, useRef, useState } from "react";

interface UseScrollAnimationOptions {
  /** Threshold for triggering animation (0-1) */
  threshold?: number;
  /** Root margin for early/late triggering */
  rootMargin?: string;
  /** Whether to only trigger once */
  triggerOnce?: boolean;
}

/**
 * @example
 * const { ref, isVisible } = useScrollAnimation();
 * return <div ref={ref} style={{ opacity: isVisible ? 1 : 0 }}>Content</div>
 */
export function useScrollAnimation(options: UseScrollAnimationOptions = {}) {
  const { threshold = 0.1, rootMargin = "0px 0px -50px 0px", triggerOnce = true } = options;
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce && ref.current) {
            observer.unobserve(ref.current);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [threshold, rootMargin, triggerOnce]);

  return { ref, isVisible };
}
